import { ChangeDetectionStrategy, Component, Inject, OnInit } from '@angular/core';
import { MatLegacyDialogRef, MAT_LEGACY_DIALOG_DATA } from '@angular/material/legacy-dialog';
import { ExprCall } from '@mode-switch/express';
import { EncField, Encoding } from '@mode-vegas/vegas';
import { DataLimitsResults } from '@mode/charts/ui';
import { ChartDataErrorReason } from '@mode/charts/util-muze';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import { getDefaultTextFormatOptions, getTextFormatOptionsByShortcutType, numberFormatter } from '@mode/shared/util-js';
import { CompiledAttr, CompiledField, CompiledFormula } from '@mode/shared/util-switch';
import * as FieldHelpers from '../helpers/field-helpers';

export enum DataLimitsAction {
  Undo = 'Undo',
  ManualUpdate = 'ManualUpdate',
  Filter = 'Filter',
  AutoAggregate = 'AutoAggregate',
  DisplayError = 'DisplayError',
}

export interface DataLimitsModalData {
  view: FlamingoTypes.ModeView;
  encoding: Encoding;
  dataResults: DataLimitsResults;
  canUndo: boolean;
  canAggregatePositionalChannel: boolean;
  applyAction: (
    selectedAction: DataLimitsAction,
    results: DataLimitsResults,
    channel?: string,
    aggr?: ExprCall.Name,
    compiledAttr?: CompiledFormula
  ) => void;
}

type FilterFieldOpt = FieldHelpers.IOpt<CompiledAttr>;

@Component({
  selector: 'mode-data-limits-modal',
  templateUrl: './data-limits-modal.component.html',
  styleUrls: ['./data-limits-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataLimitsModalComponent implements OnInit {
  readonly DataLimitsAction = DataLimitsAction;

  private autoAggregateField: CompiledField | undefined;
  private view: FlamingoTypes.ModeView;
  private encoding: Encoding;
  private dataResults: DataLimitsResults;
  private applyAction: (
    selectedAction: DataLimitsAction,
    results: DataLimitsResults,
    channel?: string,
    aggregation?: ExprCall.Name,
    compiledAttr?: CompiledFormula
  ) => void;

  description = '';
  canUndo = false;
  canAggregatePositionalChannel = false;
  canAutoAggregate = false;
  fieldName: string | undefined;
  encodingFields: ReadonlyArray<FilterFieldOpt> = [];
  otherFields: ReadonlyArray<FilterFieldOpt> = [];
  filterField: FilterFieldOpt | undefined;
  selectedAction: DataLimitsAction = DataLimitsAction.AutoAggregate;
  actionButtonText = 'Apply';

  constructor(
    private dialogRef: MatLegacyDialogRef<DataLimitsModalComponent>,
    @Inject(MAT_LEGACY_DIALOG_DATA) data: DataLimitsModalData
  ) {
    this.view = data.view;
    this.encoding = data.encoding;
    this.dataResults = data.dataResults;
    this.canUndo = data.canUndo;
    this.canAggregatePositionalChannel = data.canAggregatePositionalChannel;
    this.applyAction = data.applyAction;
  }

  ngOnInit(): void {
    // Determine if there is a dimension with a large discrete count which could
    // be aggregated.
    const autoAggEncField = this.determineAutoAggregateEncField();
    if (autoAggEncField != null) {
      this.autoAggregateField = this.compileField(this.view, autoAggEncField);
      this.fieldName = this.autoAggregateField.displayName;
      this.canAutoAggregate = true;
    }
    // If we can aggregate, then that is the default selection. Otherwise, filter.
    this.selectedAction = this.canAutoAggregate ? DataLimitsAction.AutoAggregate : DataLimitsAction.Filter;
    [this.encodingFields, this.otherFields] =
      this.view != null && this.encoding != null
        ? FieldHelpers.makePartitionedByFieldsLists(this.view, this.encoding)
        : [[], []];
    this.filterField = FieldHelpers.findOrFirst(
      [...this.encodingFields, ...this.otherFields],
      (attr) => attr.attrName === this.autoAggregateField?.attrName
    );
    // Construct the description based on the data limit results.
    this.description = this.makeDescription();
  }

  onChangeField = (item: FieldHelpers.CompiledAttrOpt): void => {
    this.filterField = item;
  };

  onActionChange($event: any) {
    this.actionButtonText = this.selectedAction === DataLimitsAction.Filter ? 'Next' : 'Apply';
  }

  undo() {
    this.applyAction(DataLimitsAction.Undo, this.dataResults);
    this.dialogRef.close();
  }

  apply() {
    this.applyAction(
      this.selectedAction,
      this.dataResults,
      this.selectedAction === DataLimitsAction.AutoAggregate ? this.dataResults.channel : undefined,
      this.selectedAction === DataLimitsAction.AutoAggregate ? ExprCall.Name.Count : undefined,
      this.selectedAction === DataLimitsAction.AutoAggregate
        ? this.autoAggregateField
        : this.selectedAction === DataLimitsAction.Filter
          ? this.filterField?.value
          : undefined
    );
    this.dialogRef.close();
  }

  makeDescription() {
    const textFormat = {
      ...getDefaultTextFormatOptions(),
      ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.ShortNumber),
    };
    const actual = numberFormatter(this.dataResults.actual, textFormat);
    const limit = numberFormatter(this.dataResults.limit, textFormat);

    switch (this.dataResults.reason) {
      case ChartDataErrorReason.TooManyFacets: {
        return (
          `This visualization contains ${actual} facets. ` +
          `The limit is ${limit}. ` +
          'Displaying this visualization might freeze or crash your browser.'
        );
      }
      case ChartDataErrorReason.TooManyPoints:
        return (
          `This visualization contains ${actual} data points. ` +
          `The limit is ${limit}. ` +
          'Displaying this visualization might freeze or crash your browser.'
        );

      case ChartDataErrorReason.TooManySeries:
        return (
          `The field '${this.fieldName}' contains ${actual} unique values. ` +
          `The limit is ${limit}. ` +
          'Displaying this visualization might freeze or crash your browser.'
        );
      case ChartDataErrorReason.TooManySlices:
        return (
          `This visualization contains ${actual} data slices. ` +
          `The limit is ${limit}. ` +
          'Displaying this visualization might freeze or crash your browser.'
        );
      case ChartDataErrorReason.TooManyElements: {
        return (
          `This visualization contains ${actual} visual elements. ` +
          `The limit is ${limit}. ` +
          'Displaying this visualization might freeze or crash your browser.'
        );
      }
      case ChartDataErrorReason.TooManyPageRows: {
        return (
          `This page contains ${actual} rows of data. ` +
          `The limit is ${limit}. ` +
          'Displaying this visualization might freeze or crash your browser.'
        );
      }
      default: {
        return '';
      }
    }
  }

  determineAutoAggregateEncField() {
    // Figure out which field is the best candidate for auto-aggregation.
    //  If there is a channel field use that.
    if (this.dataResults.reason === ChartDataErrorReason.TooManySeries && this.dataResults.fieldId) {
      const encField = EncodingHelpers.getEncFieldById(this.encoding, this.dataResults.fieldId);
      return encField;
    }
    // If there are too many facets or elements, find the field with the highest discrete count
    if (this.canAggregatePositionalChannel && this.isHighDiscreteComplexityReason(this.dataResults.reason)) {
      const maxCardinalityField = [
        ...this.encoding.column,
        ...this.encoding.row,
        ...this.encoding.x,
        ...this.encoding.y,
      ].reduce(
        (result, encField) => {
          const discreteCount = this.dataResults.stats.discreteCount?.[encField.id || ''] ?? 0;
          return discreteCount > result.count ? { fieldId: encField.id || '', count: discreteCount } : result;
        },
        { fieldId: '', count: 0 }
      );
      const encField = EncodingHelpers.getEncFieldById(this.encoding, maxCardinalityField.fieldId);
      return encField;
    }
  }

  isHighDiscreteComplexityReason(reason: ChartDataErrorReason) {
    return (
      reason === ChartDataErrorReason.TooManyFacets ||
      reason === ChartDataErrorReason.TooManyElements ||
      reason === ChartDataErrorReason.TooManyPageRows
    );
  }

  compileField(view: FlamingoTypes.ModeView, field: EncField): CompiledField {
    return CompiledField.fromEncField(view, field);
  }
}
